import { useQuery } from '@tanstack/react-query';
import { qk, type Property, type Tenancy, type Unit } from '@immoaktas/api-client';
import { floorNameKey } from '@immoaktas/shared';
import { api } from '../../../../lib/api';
import { today } from '../../../../lib/hooks';

/** Data of one unit screen (design/Wohnung): unit, property, tenancies (current / planned / earlier), meters, proposals. */
export function useUnitData(id: string) {
  const unit = useQuery({ queryKey: qk.unit(id), queryFn: () => api.units.get(id) });
  const propertyId = unit.data?.propertyId ?? '';
  const property = useQuery({ queryKey: qk.property(propertyId), queryFn: () => api.properties.get(propertyId), enabled: !!propertyId });
  const tenancies = useQuery({ queryKey: [...qk.unit(id), 'tenancies'], queryFn: () => api.units.tenancies(id) });
  const meters = useQuery({ queryKey: [...qk.meters(propertyId), id], queryFn: () => api.meters.list(propertyId, id), enabled: !!propertyId });
  const proposed = useQuery({ queryKey: qk.readings({ propertyId, status: 'proposed' }), queryFn: () => api.meters.readings({ propertyId, status: 'proposed' }), enabled: !!propertyId });
  const list = tenancies.data ?? [];
  const d = today();
  const current = list.find((t) => t.start <= d && (!t.end || t.end >= d));
  const planned = list.filter((t) => t.start > d).sort((a, b) => a.start.localeCompare(b.start));
  const earlier = list.filter((t) => t.end && t.end < d).sort((a, b) => b.start.localeCompare(a.start));
  const meterIds = new Set((meters.data ?? []).map((m) => m.id));
  const unitProposals = (proposed.data ?? []).filter((r) => meterIds.has(r.meterId));
  return { unit, property, tenancies, meters, proposals: unitProposals, current, planned, earlier };
}
export type UnitData = ReturnType<typeof useUnitData>;
export type UnitCtx = { unit: Unit; property: Property; tenancies: Tenancy[]; current?: Tenancy; planned: Tenancy[]; earlier: Tenancy[] };

type TFn = (key: string, opts?: Record<string, unknown>) => string;
/** "EG" → "Erdgeschoss", "1. OG" → "1. Obergeschoss" (labels stay as typed when unknown). */
export function floorLong(t: TFn, label: string) {
  const k = floorNameKey(label);
  return k ? t(`objects.floorNames.${k.key}`, { n: k.n }) : label.trim();
}
/** Names of a tenancy for headings: "Emre & Aylin Yilmaz" style is up to the data; two parties joined with "&". */
export const partiesText = (names: string[] | undefined) => {
  const l = (names ?? []).filter(Boolean);
  return l.length <= 2 ? l.join(' & ') : l.join(', ');
};
/** Heading of a tenancy: a flatshare as "WG Arslan · Brandt · Okafor", otherwise the party names. */
export const tenancyTitle = (t: TFn, x: { kind: string; current: { partyNames?: string[] } }) =>
  x.kind === 'shared_flat' ? t('objects.sharedFlat', { names: (x.current.partyNames ?? []).map((n) => n.split(' ').pop()).join(' · ') }) : partiesText(x.current.partyNames);
